import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormUnderlag, ValutakursParam } from '../shared/classes/form-underlag.class';
import { BFFApiService } from '../../core/http/bff-api.service';
import { FormDate } from '../shared/classes/form-date.class';
import { Inkomsttyp } from '../shared/enums/inkomsttyp.enum';
import { DinaUppgifter } from '../shared/models/dina-uppgifter.model';
import { ListItem } from '../shared/classes/list-item.class';
import { RegelmotorInput } from '../shared/models/regelmotorInput.model';
import { SammanstallningStoreService } from '../stores/sammanstallning.store';
import { InkomstTableItem } from '../shared/models/inkomst-table-item.model';
import { ResultatData } from '../shared/models/resultat-data.model';
import { ResultatStoreService } from '../stores/resultat.store';
import { DateService } from './date/date.service';
import { FormService } from './form/form.service';
import { LoadingService } from './loading/loading.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorType } from './error/errorType';

@Injectable({
  providedIn: 'root'
})
export class DinaUppgifterService {
  datumHelpTexts: string[] = [
    'Välj datumet för när du fick utbetalningen. Om du inte vet månaden eller dagen så kan du välja "Okänd". ' +
    'Under löpande år kan du bara välja ”Okänd” för dag.',
    'Tjänsten använder Riksbankens valutakurser. Om du väljer okänd månad  kommer tjänsten att använda årsgenomsnittet för valutan. ' +
    'Om du väljer okänd dag kommer tjänsten att använda månadsgenomsnittet för valutan.'
  ];

  private id = 0;
  underlagList = [];
  private landerUtdelning: ListItem[] = [];
  private landerRanta: ListItem[] = [];
  private ar: string;

  private readonly _edit = new Subject<any>();
  edit$ = this._edit.asObservable();

  private readonly _removedSammanstallningItem = new Subject<any>();
  removedSammanstallningItem$ = this._removedSammanstallningItem.asObservable();

  private readonly _resetForm = new Subject<any>();
  resetForm$ = this._resetForm.asObservable();

  private readonly _onUpdate = new Subject<any>();
  onUpdate$ = this._onUpdate.asObservable();

  private readonly _inkomstar = new BehaviorSubject<string>('');
  inkomstar$ = this._inkomstar.asObservable();

  get inkomstar(): string {
    return this._inkomstar.getValue();
  }

  set inkomstar(val: string) {
    this._inkomstar.next(val);
  }

  get loading$() {
    return this.loadingService.loading$;
  }

  constructor(
    private readonly bffApiService: BFFApiService,
    private readonly sammanstallningStoreService: SammanstallningStoreService,
    public resultatStoreService: ResultatStoreService,
    private readonly dateService: DateService,
    private readonly formService: FormService,
    private loadingService: LoadingService
  ) {
  }

  getUnderlagItem(id: number): FormUnderlag {
    const underlag = this.underlagList.find(x => x.id === id);
    return FormUnderlag.createUnderlag(underlag);
  }

  getDittUnderlag(): Promise<DinaUppgifter> {
    return this.bffApiService.getDittUnderlag()
      .then((viewModel: DinaUppgifter) => {
        this.landerUtdelning = viewModel.landerUtdelning;
        this.landerRanta = viewModel.landerRanta;
        viewModel.utbetalningsdatumAr = this.dateService.getYearsAsListItems();
        return viewModel;
      });
  }

  getInfotexter(inkomsttyp: Inkomsttyp, landskod: string, utbetalningsar: number): Promise<DinaUppgifter> {
    return this.bffApiService.getInfotexter({inkomsttyp, landskod, utbetalningsar})
      .then((viewModel: DinaUppgifter) => viewModel);
  }

  getLand(kod: string, inkomsttyp: Inkomsttyp): ListItem | undefined {
    if (inkomsttyp === Inkomsttyp.UTDELNING) {
      return this.landerUtdelning.find(x => x.value === kod);
    } else if (inkomsttyp === Inkomsttyp.RANTA) {
      return this.landerRanta.find(x => x.value === kod);
    }
  }

  convertToStringDate(formDate: FormDate): string {
    const fDate = new FormDate(formDate);
    return fDate.getDate();
  }

  isEmpty(value): boolean {
    if (value === undefined || value === null || value === '') {
      return true;
    }
    return false;
  }

  isBeforeFirstBankDayOfNewYear(): boolean {
    return this.dateService.isBeforeFirstBankDayOfNewYear();
  }

  isFormDirty(): boolean {
    return this.formService.isDirty();
  }

  addUnderlag(underlag: FormUnderlag): void {
    this.loadingService.loading = true;

    ++this.id;
    underlag.id = this.id;

    this.inkomstar = underlag.utbetalningsdatum.year;

    this.ar = underlag.utbetalningsdatum.year;
    const valutaParam = new ValutakursParam(underlag.beloppUtbetald.valuta, underlag.utlandskSkatt.valuta, underlag.utbetalningsdatum);

    this.bffApiService.getValutakurser(valutaParam).then(
      response => {
        console.log('HTTP response', response);
        underlag.beloppUtbetald.valutakurs = response.valutakurs;
        underlag.utlandskSkatt.valutakurs = response.valutakursSkatt;
        underlag.bankdag = response.bankdag;
        underlag.valutadag = response.valutadag;
        this.underlagList = [
          ...this.underlagList, FormUnderlag.createUnderlag(underlag)
        ];
        this.sammanstallningStoreService.addItem(this.mapToInkomstTableItem(FormUnderlag.createUnderlag(underlag)));
        this.loadingService.loading = false;
      },
      (err: HttpErrorResponse) => {
        console.log('HTTP Error', err);
        if (err.error.name === ErrorType.VALUTAKURS_VALUTAKURS_ERROR) {
          this.inkomstar = undefined;
          this.resetForm();
        }
        this.loadingService.loading = false;
      }
    );
  }

  mapToInkomstTableItem(u: FormUnderlag): InkomstTableItem {
    return {
      id: u.id,
      inkomsttyp: u.inkomsttyp === Inkomsttyp.UTDELNING ? 'Utdelning' : 'Ränta',
      land: this.getLand(u.land, u.inkomsttyp).text,
      datum: this.convertToStringDate(u.utbetalningsdatum),
      valuta: this.isEmpty(u.beloppUtbetald.valuta) ? '' : u.beloppUtbetald.valuta,
      beloppUtbetald: this.isEmpty(u.beloppUtbetald.belopp) ? '' : this.removeSpaceChar(u.beloppUtbetald.belopp),
      utlandskSkatt: this.isEmpty(u.utlandskSkatt.valuta) ? '' : u.utlandskSkatt.valuta,
      skatt: this.isEmpty(u.utlandskSkatt.belopp) ? '' : this.removeSpaceChar(u.utlandskSkatt.belopp),
      isSelected: false,
    };
  }

  deselectCurrentItem(): void {
    this.sammanstallningStoreService.deselectCurrentItem();
  }

  removeUnderlag(id: number): void {
    this.underlagList = this.underlagList.filter(x => x.id !== id);
    this.sammanstallningStoreService.removeItem(id);
    this._removedSammanstallningItem.next(id);

    if (this.underlagList.length <= 0) {
      this.inkomstar = undefined;
      this._resetForm.next();
    }
  }

  updateUnderlag(underlag: FormUnderlag): void {
    this.loadingService.loading = true;
    const item = this.underlagList.find(x => x.id === underlag.id);
    const index = this.underlagList.indexOf(item);

    const valutaParam =
      new ValutakursParam(underlag.beloppUtbetald.valuta, underlag.utlandskSkatt.valuta, underlag.utbetalningsdatum);
    this.bffApiService.getValutakurser(valutaParam).then((response: any) => {
        console.log('HTTP response', response);
        underlag.beloppUtbetald.valutakurs = response.valutakurs;
        underlag.utlandskSkatt.valutakurs = response.valutakursSkatt;
        underlag.bankdag = response.bankdag;
        underlag.valutadag = response.valutadag;
        this.underlagList[index] = FormUnderlag.createUnderlag(underlag);
        this.sammanstallningStoreService.updateItem(this.mapToInkomstTableItem(underlag));
        this._onUpdate.next(item.id);
        this.loadingService.loading = false;
      },
      (err: HttpErrorResponse) => {
        console.log('HTTP Error', err);
        if (err.error.name === ErrorType.VALUTAKURS_VALUTAKURS_ERROR) {
          this.inkomstar = undefined;
          this.resetForm();
        }
        this._onUpdate.next(item.id);
        this.loadingService.loading = false;
      }
    );
  }

  edit(id: number): void {
    this._edit.next(this.getUnderlagItem(id));
  }

  resetForm(): void {
    this._resetForm.next();
  }

  clearAll(): void {
    this.inkomstar = undefined;
    this._resetForm.next();
    this.clearUnderlagList();
    this.sammanstallningStoreService.clearList();
  }

  calculate(): PromiseLike<void> {
    const inkomster = this.underlagList.map((item: any) => ({
        inkomsttyp: item.inkomsttyp,
        landskod: item.land,
        utbetalningsdatum: this.convertToStringDate(item.utbetalningsdatum),
        utlandskInkomst: item.beloppUtbetald.belopp,
        utlandskSkatt: item.utlandskSkatt.belopp,
        valuta: {
          valutakod: item.beloppUtbetald.valuta,
          valutakurs: item.beloppUtbetald.valutakurs,
          valutadag: item.valutadag,
          bankdag: item.bankdag,
        },
        skattValuta: {
          valutakod: item.utlandskSkatt.valuta,
          valutakurs: item.utlandskSkatt.valutakurs,
          valutadag: item.valutadag,
          bankdag: item.bankdag,
        }
      }));

    const regelmotorInput: RegelmotorInput = {
      inkomster
    };

    return this.bffApiService.getResultat(regelmotorInput)
      .then((resultatData: ResultatData) => {
        this.resultatStoreService.update(resultatData);
      });
  }

  private clearUnderlagList(): void {
    this.underlagList = undefined;
    this.underlagList = [];
  }

  private removeSpaceChar(str: string): string {
    return str.replace(/\s/g, '');
  }

}
